var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { bool, clamp, createAutoGroup, num, setBoolRefresh, setParts, setRefresh, setState, str, } from '../helpers';
import { autoCenteredText, centerInBounds, fitTextX, fluidFont, isCompactBounds, resolveBounds, resolveDisplay } from '../layout';
import { themeFromProps } from '../themes';
import { buildDialGauge, updateDialNeedle } from '../../primitives/dialGauge';
import { buildDigitalGauge, digitalGaugeStyle, updateDigitalGauge, } from './digitalGauge';
var DIAL_LABELS = {
    speedometer: 'Speed',
    tachometer: 'RPM',
    turboBoostGauge: 'Boost',
    torqueMeter: 'Torque',
    horsepowerMeter: 'HP',
    engineLoad: 'Load',
    throttlePosition: 'Throttle',
    brakePressure: 'Brake',
    steeringAngle: 'Steer',
    yawRate: 'Yaw',
    altimeter: 'Alt',
    oilPressure: 'Oil',
    powerMeter: 'kW',
    gForceMeter: 'G',
    engineTemp: 'Temp',
    engineTemperature: 'Engine',
    coolantTemperature: 'Coolant',
    oilTemperature: 'Oil',
};
function dialLabel(type) {
    if (DIAL_LABELS[type])
        return DIAL_LABELS[type];
    var words = type.replace(/([A-Z])/g, ' $1').trim().split(/\s+/);
    if (words.length <= 2)
        return words.join(' ');
    return words.map(function (w) { var _a, _b; return (_b = (_a = w[0]) === null || _a === void 0 ? void 0 : _a.toUpperCase()) !== null && _b !== void 0 ? _b : ''; }).join('');
}
export function formatValue(v, format, unit) {
    if (unit === void 0) { unit = ''; }
    switch (format) {
        case 'rpm':
            return "".concat(Math.round(v / 1000), "k");
        case 'percent':
            return "".concat(Math.round(v), "%");
        case 'deg':
            return "".concat(Math.round(v), "\u00B0");
        case 'volt':
            return "".concat(v.toFixed(1), "V");
        case 'psi':
            return "".concat(Math.round(v), " PSI");
        case 'text':
            return String(v);
        default:
            return unit ? "".concat(Math.round(v)).concat(unit) : String(Math.round(v));
    }
}
export function buildDialWidget(app, type, autoPart, props, options) {
    var _a, _b;
    var theme = themeFromProps(props);
    var bounds = resolveBounds(props, 160, 160);
    var value = num(props, 'value', 0);
    var max = num(props, 'max', options.max);
    var fmt = function (v) { return formatValue(v, options.format); };
    var display = resolveDisplay(props, 'analog');
    if (display === 'analog' && isCompactBounds(bounds))
        display = 'digital';
    if (display === 'digital') {
        var group_1 = createAutoGroup(app, type, __assign(__assign({}, props), { width: bounds.width, height: bounds.height, display: 'digital' }), autoPart);
        var style = digitalGaugeStyle(theme);
        var digitalFmt_1 = options.format === 'rpm'
            ? function (v) { return String(Math.round(v)); }
            : function (v) { return String(Math.round(v)); };
        var digitalUnit = options.format === 'rpm' ? 'RPM' : options.unit;
        var parts_1 = buildDigitalGauge(app, group_1, bounds, style, {
            label: dialLabel(autoPart),
            value: value,
            max: max,
            unit: digitalUnit,
            formatValue: digitalFmt_1,
            showBar: !isCompactBounds(bounds),
            showSegments: !isCompactBounds(bounds) && (autoPart === 'tachometer' || options.format === 'rpm'),
            segmentCount: 10,
        });
        var barW_1 = bounds.innerWidth - bounds.pad;
        setParts(group_1, { valueText: parts_1.valueText });
        group_1.metadata._digitalParts = parts_1;
        setRefresh(group_1, function (v) {
            var _a;
            updateDigitalGauge(parts_1, v, max, digitalFmt_1, barW_1, (_a = parts_1.segments) === null || _a === void 0 ? void 0 : _a.length);
        });
        setState(group_1, { width: bounds.width, height: bounds.height, value: value, max: max, display: 'digital' });
        return group_1;
    }
    var maxFit = Math.min(bounds.innerWidth, bounds.innerHeight) - 6;
    var size = Math.min(bounds.dialSize, maxFit);
    var needleColor = str(props, 'needleColor', (_a = options.needleColor) !== null && _a !== void 0 ? _a : theme.accent);
    var group = createAutoGroup(app, type, __assign(__assign({}, props), { width: bounds.width, height: bounds.height, size: size, display: 'analog' }), autoPart);
    var cx = size / 2;
    var inset = Math.max(4, Math.min(12, size * 0.1));
    var r = size / 2 - inset;
    var origin = centerInBounds(bounds, size, size);
    var inner = app.group({ x: origin.x, y: origin.y, listening: false });
    group.add(inner);
    var parts = buildDialGauge(app, inner, {
        trackColor: theme.dialStroke,
        needleColor: needleColor,
        accentColor: needleColor,
        textColor: theme.text,
        textMuted: theme.textMuted,
        faceColor: '#0a0a0a',
        bezelColor: theme.dialStroke,
        redlineColor: theme.warning,
        tickColor: theme.textMuted,
        tickLabelColor: theme.textMuted,
    }, {
        size: size,
        value: value,
        max: max,
        title: dialLabel(autoPart),
        formatValue: fmt,
        formatTickLabel: options.format === 'rpm'
            ? function (v) { return String(Math.round(v / 1000)); }
            : options.format === 'percent'
                ? function (v) { return String(Math.round(v)); }
                : function (v) { return String(Math.round(v)); },
        unit: options.unit,
        tickCount: (_b = options.tickCount) !== null && _b !== void 0 ? _b : (size < 100 ? 5 : 10),
        showTickLabels: size >= 96,
        redlineFrom: options.redlineFrom,
    });
    setParts(group, { needle: parts.needle, label: parts.valueText, inner: inner });
    setRefresh(group, function (v) {
        updateDialNeedle(parts.needle, cx, v, max, r, undefined, undefined, undefined, parts.valueArc);
        parts.valueText.text = fmt(v);
    });
    setState(group, { width: bounds.width, height: bounds.height, size: size, value: value, max: max, display: 'analog' });
    return group;
}
export function buildBarWidget(app, type, autoPart, props, options) {
    var _a;
    var theme = themeFromProps(props);
    var value = clamp(num(props, 'value', 50), 0, 100);
    var bounds = resolveBounds(props, 120, 56);
    var group = createAutoGroup(app, type, __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), autoPart);
    var w = bounds.innerWidth;
    var h = bounds.innerHeight;
    var titleSize = fluidFont(9, bounds, 7, 11);
    var valueSize = fluidFont(14, bounds, 11, 18);
    var trackH = Math.max(6, Math.round(h * 0.14));
    var trackY = h - trackH - Math.max(6, bounds.pad * 0.5);
    var trackW = w - bounds.pad;
    group.add(app.roundedRect({
        width: w,
        height: h,
        cornerRadius: Math.min(10, h * 0.15),
        fill: '#111827',
        stroke: theme.dialStroke,
        strokeWidth: 1,
        listening: false,
    }), app.text({
        text: options.label.toUpperCase(),
        fontSize: titleSize,
        fontWeight: '600',
        fill: theme.textMuted,
        x: bounds.pad * 0.5,
        y: bounds.pad * 0.4,
        listening: false,
    }));
    var track = app.roundedRect({
        x: bounds.pad * 0.5,
        y: trackY,
        width: trackW,
        height: trackH,
        fill: theme.lampOff,
        cornerRadius: trackH / 2,
        listening: false,
    });
    var fill = app.roundedRect({
        x: bounds.pad * 0.5,
        y: trackY,
        width: (trackW * value) / 100,
        height: trackH,
        fill: options.warnBelow !== undefined && value < options.warnBelow ? theme.warning : theme.ok,
        cornerRadius: trackH / 2,
        listening: false,
    });
    var label = autoCenteredText(app, "".concat(value).concat((_a = options.unit) !== null && _a !== void 0 ? _a : '%'), w, h * 0.4, {
        fontSize: valueSize,
        fontWeight: 'bold',
        fill: theme.text,
    });
    group.add(track, fill, label);
    setParts(group, { fill: fill, label: label, track: track });
    setRefresh(group, function (v) {
        var _a;
        var lv = clamp(v, 0, 100);
        fill.width = (trackW * lv) / 100;
        fill.fill =
            options.warnBelow !== undefined && lv < options.warnBelow ? theme.warning : theme.ok;
        label.text = "".concat(Math.round(lv)).concat((_a = options.unit) !== null && _a !== void 0 ? _a : '%');
    });
    setState(group, { value: value, width: bounds.width, height: bounds.height });
    return group;
}
export function buildNumericWidget(app, type, autoPart, props, options) {
    var _a, _b, _c;
    var theme = themeFromProps(props);
    var value = num(props, 'value', 0);
    var text = str(props, 'text', '');
    var bounds = resolveBounds(props, (_a = options.width) !== null && _a !== void 0 ? _a : 128, 60);
    var group = createAutoGroup(app, type, __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), autoPart);
    var dec = (_b = options.decimals) !== null && _b !== void 0 ? _b : (options.unit === 'V' ? 1 : 0);
    var isLcd = resolveDisplay(props, 'analog') === 'digital' ||
        ['digitalClock', 'dateDisplay', 'odometer', 'tripMeter', 'eta', 'lapTimer', 'accelerationTimer'].includes(type);
    if (isLcd) {
        var style = digitalGaugeStyle(theme);
        var displayText = text || "".concat(value.toFixed(dec));
        var parts_2 = buildDigitalGauge(app, group, bounds, style, {
            label: options.title,
            value: value,
            max: Math.pow(10, Math.max(3, displayText.length)) - 1,
            unit: options.unit,
            formatValue: function (v) { return (text ? text : "".concat(v.toFixed(dec))); },
            showBar: false,
            showSegments: false,
        });
        if (text)
            parts_2.valueText.text = text;
        setParts(group, { valueText: parts_2.valueText });
        setRefresh(group, function (v) {
            var _a;
            parts_2.valueText.text = "".concat(v.toFixed(dec)).concat((_a = options.unit) !== null && _a !== void 0 ? _a : '');
        });
        group.metadata.textRefresh = function (t) {
            parts_2.valueText.text = t;
        };
        setState(group, { value: value, text: text, width: bounds.width, height: bounds.height, display: 'digital' });
        return group;
    }
    var w = bounds.innerWidth;
    var h = bounds.innerHeight;
    var titleSize = fluidFont(8, bounds, 7, 10);
    var valueSize = fluidFont(18, bounds, 13, 22);
    group.add(app.roundedRect({
        width: w,
        height: h,
        cornerRadius: Math.min(10, h * 0.14),
        fill: '#111827',
        stroke: theme.dialStroke,
        strokeWidth: 1,
        listening: false,
    }), app.text({
        text: options.title.toUpperCase(),
        x: bounds.pad * 0.5,
        y: bounds.pad * 0.35,
        fontSize: titleSize,
        fontWeight: 'bold',
        fill: theme.textMuted,
        listening: false,
    }));
    var label = autoCenteredText(app, text || "".concat(value.toFixed(dec)).concat((_c = options.unit) !== null && _c !== void 0 ? _c : ''), w, h * 0.58, { fontSize: valueSize, fontWeight: 'bold', fill: theme.text });
    group.add(label);
    setParts(group, { label: label });
    setRefresh(group, function (v) {
        var _a;
        label.text = "".concat(v.toFixed(dec)).concat((_a = options.unit) !== null && _a !== void 0 ? _a : '');
    });
    group.metadata.textRefresh = function (t) {
        label.text = t;
    };
    setState(group, { value: value, text: text, width: bounds.width, height: bounds.height });
    return group;
}
export function buildLampWidget(app, type, autoPart, props, symbol, options) {
    var _a, _b, _c, _d;
    var active = bool(props, 'active', false);
    var theme = themeFromProps(props);
    var bounds = resolveBounds(props, 36, 36);
    var group = createAutoGroup(app, type, __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), autoPart);
    var maxR = Math.min(bounds.innerWidth, bounds.innerHeight) / 2 - 3;
    var radius = Math.max(10, Math.min(maxR, 48));
    var fontSize = fluidFont(symbol.length > 3 ? 7 : 10, bounds, 6, 12);
    var center = centerInBounds(bounds, radius * 2, radius * 2);
    var symW = radius * 2;
    var onFill = (_a = options === null || options === void 0 ? void 0 : options.onFill) !== null && _a !== void 0 ? _a : theme.lampOn;
    var onStroke = (_b = options === null || options === void 0 ? void 0 : options.onStroke) !== null && _b !== void 0 ? _b : '#fde047';
    var onText = (_c = options === null || options === void 0 ? void 0 : options.onText) !== null && _c !== void 0 ? _c : '#111';
    var onShadow = (_d = options === null || options === void 0 ? void 0 : options.onShadow) !== null && _d !== void 0 ? _d : 'rgba(251,191,36,0.5)';
    var lamp = app.circle({
        radius: radius,
        x: center.x,
        y: center.y,
        fill: active ? onFill : theme.lampOff,
        stroke: active ? onStroke : '#555',
        strokeWidth: 1,
        shadow: active ? { color: onShadow, blur: 8, offsetX: 0, offsetY: 0 } : undefined,
        listening: false,
    });
    var sym = app.text({
        text: symbol,
        x: center.x + fitTextX(symbol, fontSize, symW),
        y: center.y + radius,
        fontSize: fontSize,
        fill: active ? onText : '#666',
        textAlign: 'left',
        textBaseline: 'middle',
        listening: false,
    });
    group.add(lamp, sym);
    setParts(group, { lamp: lamp, sym: sym });
    setBoolRefresh(group, function (on) {
        lamp.fill = on ? onFill : theme.lampOff;
        lamp.stroke = on ? onStroke : '#555';
        sym.fill = on ? onText : '#666';
        lamp.shadow = on
            ? { color: onShadow, blur: 8, offsetX: 0, offsetY: 0 }
            : null;
    });
    setState(group, { active: active, width: bounds.width, height: bounds.height });
    return group;
}
export function buildBadgeWidget(app, type, autoPart, props, title) {
    var _a;
    var status = str(props, 'status', str(props, 'text', 'OFF'));
    var active = bool(props, 'active', status.toLowerCase() === 'on' || status.toLowerCase() === 'active');
    var theme = themeFromProps(props);
    var bounds = resolveBounds(props, 168, 52);
    var group = createAutoGroup(app, type, __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), autoPart);
    var w = bounds.innerWidth;
    var h = bounds.innerHeight;
    var badgeH = Math.max(22, Math.round(h * 0.36));
    var colors = {
        off: '#333',
        on: theme.ok,
        active: theme.ok,
        standby: theme.warning,
        fault: theme.warning,
        error: theme.warning,
        connected: theme.ok,
        disconnected: '#333',
        clear: '#333',
        detecting: theme.accent,
        warning: theme.warning,
        alert: theme.warning,
    };
    var key = status.toLowerCase();
    var titleSize = fluidFont(9, bounds, 7, 10);
    var titleH = titleSize + 8;
    var stackH = titleH + 6 + badgeH;
    var stackY = bounds.pad + Math.max(0, (h - stackH) / 2);
    var badgeY = stackY + titleH + 4;
    group.add(app.roundedRect({
        width: w,
        height: h,
        cornerRadius: Math.min(8, h * 0.12),
        fill: '#111827',
        stroke: theme.dialStroke,
        strokeWidth: 1,
        listening: false,
    }));
    var bg = app.roundedRect({
        width: w,
        height: badgeH,
        y: badgeY,
        cornerRadius: 4,
        fill: active ? theme.ok : ((_a = colors[key]) !== null && _a !== void 0 ? _a : '#333'),
        listening: false,
    });
    var label = autoCenteredText(app, status.toUpperCase(), w, badgeY + badgeH / 2, {
        fontSize: fluidFont(10, bounds, 8, 12),
        fontWeight: 'bold',
        fill: '#fff',
    });
    var cap = autoCenteredText(app, title.length > 18 ? title.slice(0, 17) + '…' : title, w, stackY + titleH / 2, {
        fontSize: titleSize,
        fill: theme.textMuted,
    });
    group.add(cap, bg, label);
    setParts(group, { bg: bg, label: label });
    group.metadata.textRefresh = function (t) {
        var _a;
        label.text = t.toUpperCase();
        var k = t.toLowerCase();
        bg.fill = (_a = colors[k]) !== null && _a !== void 0 ? _a : (t ? theme.ok : '#333');
    };
    group.metadata.boolRefresh = function (on) {
        bg.fill = on ? theme.ok : '#333';
        label.text = on ? 'ON' : 'OFF';
    };
    setState(group, { status: status, active: active, width: bounds.width, height: bounds.height });
    return group;
}
export function buildInfoPanel(app, type, autoPart, props, title, rows) {
    var _a;
    if (rows === void 0) { rows = []; }
    var theme = themeFromProps(props);
    var lines = (_a = props.lines) !== null && _a !== void 0 ? _a : rows;
    var bounds = resolveBounds(props, 200, Math.max(72, lines.length * 18 + 32));
    var group = createAutoGroup(app, type, __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), autoPart);
    var w = bounds.innerWidth;
    var h = bounds.innerHeight;
    var titleSize = fluidFont(9, bounds, 7, 11);
    var rowSize = fluidFont(10, bounds, 7, 11);
    var titleH = titleSize + 8;
    var maxRows = Math.max(1, Math.floor((h - titleH - 4) / 11));
    var visibleLines = lines.slice(0, maxRows);
    var rowH = Math.max(11, Math.floor((h - titleH - 4) / Math.max(visibleLines.length, 1)));
    group.add(app.roundedRect({
        width: w,
        height: h,
        cornerRadius: Math.min(10, h * 0.1),
        fill: '#111827',
        stroke: theme.dialStroke,
        strokeWidth: 1,
        listening: false,
    }), app.text({
        text: title.toUpperCase(),
        x: bounds.pad * 0.5,
        y: bounds.pad * 0.4,
        fontSize: titleSize,
        fontWeight: 'bold',
        fill: theme.textMuted,
        listening: false,
    }));
    var rowNodes = [];
    visibleLines.forEach(function (line, i) {
        var row = app.text({
            text: line.length > 22 ? line.slice(0, 21) + '…' : line,
            x: bounds.pad * 0.5,
            y: titleH + i * rowH,
            fontSize: rowSize,
            fill: theme.text,
            listening: false,
        });
        rowNodes.push(row);
        group.add(row);
    });
    group.metadata.linesRefresh = function (next) {
        next.forEach(function (line, i) {
            if (rowNodes[i])
                rowNodes[i].text = line;
        });
    };
    setState(group, { lines: lines, width: bounds.width, height: bounds.height });
    return group;
}
